import type { ComponentType, SVGProps } from 'react';

import { IllustrationGlow } from './IllustrationGlow';

import { cn } from '@/lib/utils';

export type IllustrationVariant = 'success' | 'warning' | 'error' | 'unknown';

const ARTWORK_SIZE = 200;

const centeredCanvas = (width: number, height: number) => ({
  width,
  height,
  viewBox: `${(ARTWORK_SIZE - width) / 2} ${(ARTWORK_SIZE - height) / 2} ${width} ${height}`,
});

const CANVASES = {
  default: centeredCanvas(320, 282),
  tight: centeredCanvas(ARTWORK_SIZE, ARTWORK_SIZE),
};

export interface IllustrationProps extends Omit<
  SVGProps<SVGSVGElement>,
  'name'
> {
  canvas?: keyof typeof CANVASES;
}

interface IllustrationBaseProps extends IllustrationProps {
  glyph: ComponentType<SVGProps<SVGSVGElement>>;
  name: string;
  variant: IllustrationVariant;
}

export const IllustrationBase = ({
  canvas = 'default',
  name,
  variant,
  className,
  glyph: Glyph,
  ...props
}: IllustrationBaseProps) => {
  const labelled = Boolean(props['aria-label'] ?? props['aria-labelledby']);

  return (
    <svg
      {...CANVASES[canvas]}
      {...props}
      aria-hidden={labelled ? undefined : true}
      className={cn('overflow-visible', className)}
      data-illustration={name}
      role={labelled ? 'img' : undefined}
    >
      <IllustrationGlow variant={variant} />
      <Glyph />
    </svg>
  );
};
